import React from "react";
import { createRoot, hydrateRoot } from "react-dom/client";
import "@/index.css";
import App from "@/App";
import { HelmetProvider } from "react-helmet-async";

/*
 * Cross-environment API URL guard.
 *
 * REACT_APP_BACKEND_URL is baked at build time, so a production build
 * pointing at https://mundalo.com would break the preview environment
 * (browsers reject cross-origin credentialed requests when the response
 * uses Access-Control-Allow-Origin: *, which Emergent's edge ingress sets).
 *
 * Fix: if we detect we're running on the preview hostname, transparently
 * rewrite outbound fetch URLs from the production domain back to the
 * preview's same-origin. Production keeps working unmodified.
 *
 * Also: surface a visible red banner if the baked-in REACT_APP_BACKEND_URL
 * is misconfigured for the current hostname - catches the class of bug
 * where production silently calls the preview backend (or vice versa).
 */
(() => {
  if (typeof window === "undefined") return;
  const host = window.location.hostname;
  const isPreview = host.endsWith(".preview.emergentagent.com")
    || host.endsWith(".preview.emergent.host")
    || host === "localhost"
    || host === "127.0.0.1";
  const isProd = host === "mundalo.com" || host === "www.mundalo.com";

  const PROD_RE = /^https?:\/\/(www\.)?mundalo\.com/i;
  const PREVIEW_RE = /\.preview\.(emergentagent|emergent\.host)/i;
  // Emergent's canonical production host that the deploy pipeline bakes
  // into REACT_APP_BACKEND_URL. The custom domain mundalo.com is fronted
  // by this host, so on mundalo.com we rewrite calls back to same-origin
  // (required so the admin_token cookie - set on the mundalo.com origin -
  // is sent on /api/* requests).
  const EMERGENT_PROD_RE = /^https?:\/\/[a-z0-9-]+\.emergent\.host/i;
  const backendUrl = (process.env.REACT_APP_BACKEND_URL || "").trim();

  // ---- Misconfiguration banner ----
  // Fires once on load if the baked-in backend URL contradicts the hostname
  // in a way the rewrite below cannot recover from.
  const showBanner = (msg) => {
    if (document.getElementById("__mundalo-config-banner")) return;
    const el = document.createElement("div");
    el.id = "__mundalo-config-banner";
    el.setAttribute("role", "alert");
    el.style.cssText =
      "position:fixed;top:0;left:0;right:0;z-index:99999;" +
      "background:#B91C1C;color:#fff;padding:10px 16px;" +
      "font:600 13px/1.4 ui-monospace,SFMono-Regular,Menlo,monospace;" +
      "text-align:center;box-shadow:0 2px 8px rgba(0,0,0,.2);";
    el.textContent = "⚠ CONFIG ERROR · " + msg;
    document.documentElement.style.scrollPaddingTop = "44px";
    if (document.body) {
      document.body.appendChild(el);
    } else {
      document.addEventListener("DOMContentLoaded", () => document.body.appendChild(el));
    }
    // eslint-disable-next-line no-console
    console.error("[mundalo-config]", msg, { host, backendUrl });
  };

  if (isProd && PREVIEW_RE.test(backendUrl)) {
    showBanner(
      "Production frontend is calling the PREVIEW backend (" + backendUrl +
      "). Redeploy with REACT_APP_BACKEND_URL=https://mundalo.com."
    );
  } else if (isProd && backendUrl
             && !PROD_RE.test(backendUrl)
             && !EMERGENT_PROD_RE.test(backendUrl)) {
    showBanner(
      "Production hostname but REACT_APP_BACKEND_URL is '" + backendUrl +
      "'. Expected https://mundalo.com."
    );
  } else if (isPreview && !backendUrl) {
    showBanner("REACT_APP_BACKEND_URL is empty - all API calls will 404.");
  }
  // Note: isProd + emergent.host backendUrl is recoverable via the rewrite
  // below (transparent same-origin), so no banner for that case.

  // ---- Fetch rewrite ----
  // Preview: rewrite mundalo.com URLs back to preview's same-origin.
  // Prod:    rewrite flamboyant-wing-4.emergent.host URLs back to mundalo.com
  //          same-origin (so the admin_token cookie is included).
  if (!isPreview && !isProd) return;

  const sameOrigin = window.location.origin;
  const matcher = isPreview ? PROD_RE : EMERGENT_PROD_RE;
  const rewrite = (u) => {
    if (typeof u === "string") return u.replace(matcher, sameOrigin);
    if (u && typeof u.url === "string" && matcher.test(u.url)) {
      try { return new Request(u.url.replace(matcher, sameOrigin), u); }
      catch { return u; }
    }
    return u;
  };

  const origFetch = window.fetch.bind(window);
  window.fetch = (input, init) => origFetch(rewrite(input), init);
})();

const rootElement = document.getElementById("root");
const app = (
  <React.StrictMode>
    <HelmetProvider>
      <App />
    </HelmetProvider>
  </React.StrictMode>
);

if (rootElement.hasChildNodes()) {
  hydrateRoot(rootElement, app);
} else {
  createRoot(rootElement).render(app);
}
